"use client"

import { usePathname } from "next/navigation"
import { useEffect } from "react"
import { trackError, trackEvent, trackPageView } from "../lib/telemetry"

interface TelemetryProviderProps {
  children: React.ReactNode
  userId?: string
  sessionId?: string
}

/**
 * Automatic telemetry provider - no manual tracking needed!
 * Just wrap your app and it automatically tracks:
 * - Page views
 * - JavaScript errors
 * - Promise rejections
 * - Performance metrics
 * - Network errors (via Apollo Link)
 */
export function TelemetryProvider({
  children,
  userId,
  sessionId,
}: TelemetryProviderProps) {
  const pathname = usePathname()

  useEffect(() => {
    // Automatically track page views
    if (pathname) {
      trackPageView(pathname, window.location.href, {
        userId,
        sessionId,
        customProperties: {
          "page.referrer": document.referrer,
          "page.title": document.title,
          "page.pathname": pathname,
        },
      })
    }
  }, [pathname, userId, sessionId])

  useEffect(() => {
    // Automatically track all JavaScript errors
    const handleError = (event: ErrorEvent) => {
      trackError(new Error(event.message), "Automatic Error Detection", {
        userId,
        sessionId,
        customProperties: {
          "error.filename": event.filename,
          "error.lineno": event.lineno,
          "error.colno": event.colno,
          "error.stack": event.error?.stack,
          "error.type": "javascript_error",
        },
      })
    }

    // Automatically track all unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error =
        event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason))
      trackError(error, "Automatic Promise Rejection Detection", {
        userId,
        sessionId,
        customProperties: {
          "error.reason": String(event.reason),
          "error.type": "unhandled_promise_rejection",
        },
      })
    }

    // Automatically track performance metrics
    const trackPerformanceMetrics = () => {
      if ("performance" in window && "getEntriesByType" in performance) {
        const navigation = performance.getEntriesByType(
          "navigation",
        )[0] as PerformanceNavigationTiming
        if (navigation) {
          trackEvent(
            "Page Performance",
            {
              "navigation.type": navigation.type,
              "navigation.domContentLoaded":
                navigation.domContentLoadedEventEnd -
                navigation.domContentLoadedEventStart,
              "navigation.loadComplete":
                navigation.loadEventEnd - navigation.loadEventStart,
              "navigation.firstContentfulPaint":
                navigation.responseEnd - navigation.fetchStart,
              "page.pathname": pathname,
            },
            { userId, sessionId },
          )
        }
      }
    }

    // Automatically track resource loading errors
    const handleResourceError = (event: Event) => {
      const target = event.target as HTMLElement
      if (target) {
        trackError(
          new Error(`Resource failed to load: ${target.tagName}`),
          "Automatic Resource Error Detection",
          {
            userId,
            sessionId,
            customProperties: {
              "resource.tagName": target.tagName,
              "resource.src": (target as any).src || (target as any).href,
              "error.type": "resource_error",
            },
          },
        )
      }
    }

    // Add all event listeners
    window.addEventListener("error", handleError)
    window.addEventListener("unhandledrejection", handleUnhandledRejection)
    window.addEventListener("error", handleResourceError, true) // Capture phase for resource errors

    // Track performance after page load
    if (document.readyState === "complete") {
      trackPerformanceMetrics()
    } else {
      window.addEventListener("load", trackPerformanceMetrics)
    }

    return () => {
      window.removeEventListener("error", handleError)
      window.removeEventListener("unhandledrejection", handleUnhandledRejection)
      window.removeEventListener("error", handleResourceError, true)
      window.removeEventListener("load", trackPerformanceMetrics)
    }
  }, [userId, sessionId, pathname])

  // Just return children - no context needed since everything is automatic
  return <>{children}</>
}
